import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { rpaRepository as repo } from '../../../lib/repositories/rpa.repository';
import { CHU_KY_LIEN_KET_MS, CHU_KY_LUOT_MS } from '../constants';
import type {
  BanLuuGiaTri,
  GiaTriWorkflow,
  KetQuaLuuGiaTri,
  LienKetOpenCore,
  LuotChay,
  LuotGanNhat,
  ProjectOpenRpa,
  SoDoWorkflow,
  TuyChonChay,
  WorkflowOpenCore,
} from '../types';
import { KHOA_RPA, thuLai } from './useRpa';

// ─────────────── Project, Workflow, sơ đồ ───────────────

export function useProjects(bat = true) {
  return useQuery<ProjectOpenRpa[], ApiError>({
    queryKey: KHOA_RPA.projects,
    queryFn: () => repo.projects(),
    enabled: bat,
    staleTime: 60_000,
    retry: thuLai,
  });
}

/** Nút Làm mới: bỏ bộ nhớ đệm 30 giây của máy chủ (`?fresh=1`), ghi kết quả thẳng vào cache. */
export function useLamMoiProjects() {
  const qc = useQueryClient();
  return useMutation<ProjectOpenRpa[], ApiError, void>({
    mutationFn: () => repo.projects(true),
    onSuccess: (ds) => qc.setQueryData(KHOA_RPA.projects, ds),
  });
}

export function useWorkflows(bat = true) {
  return useQuery<WorkflowOpenCore[], ApiError>({
    queryKey: KHOA_RPA.workflows,
    queryFn: () => repo.workflows(),
    enabled: bat,
    staleTime: 60_000,
    retry: thuLai,
  });
}

export function useSoDo(id: string) {
  return useQuery<SoDoWorkflow, ApiError>({
    queryKey: KHOA_RPA.soDo(id),
    queryFn: () => repo.soDo(id),
    enabled: id !== '',
    staleTime: 60_000,
    retry: thuLai,
  });
}

// ─────────────── Biến & tham số ───────────────

/** `staleTime: 0`: giá trị và `version` phải là bản mới nhất trên OpenCore, người khác có thể vừa sửa ở Designer. */
export function useGiaTri(id: string) {
  return useQuery<GiaTriWorkflow, ApiError>({
    queryKey: KHOA_RPA.giaTri(id),
    queryFn: () => repo.giaTri(id),
    enabled: id !== '',
    staleTime: 0,
    retry: thuLai,
  });
}

/** Lưu xong thì ghi bản mới vào cache; hỏng vì Workflow vừa bị sửa nơi khác (`409`) thì phía gọi tự hỏi lại. */
export function useLuuGiaTri(id: string) {
  const qc = useQueryClient();
  return useMutation<KetQuaLuuGiaTri, ApiError, { baseVersion: number | null; changes: { key: string; value: string }[] }>({
    mutationFn: (than) => repo.luuGiaTri(id, than),
    onSuccess: (moi) => {
      qc.setQueryData<GiaTriWorkflow>(KHOA_RPA.giaTri(id), moi);
      void qc.invalidateQueries({ queryKey: KHOA_RPA.lichSuGiaTri(id) });
    },
  });
}

export function useLichSuGiaTri(id: string, bat: boolean) {
  return useQuery<BanLuuGiaTri[], ApiError>({
    queryKey: KHOA_RPA.lichSuGiaTri(id),
    queryFn: () => repo.lichSuGiaTri(id),
    enabled: bat && id !== '',
    staleTime: 0,
    retry: thuLai,
  });
}

export function useKhoiPhucGiaTri(id: string) {
  const qc = useQueryClient();
  return useMutation<GiaTriWorkflow, ApiError, { backupId: string; baseVersion: number | null }>({
    mutationFn: (than) => repo.khoiPhucGiaTri(id, than),
    onSuccess: (moi) => {
      qc.setQueryData(KHOA_RPA.giaTri(id), moi);
      void qc.invalidateQueries({ queryKey: KHOA_RPA.lichSuGiaTri(id) });
      void qc.invalidateQueries({ queryKey: KHOA_RPA.soDo(id) });
    },
  });
}

// ─────────────── Tài khoản OpenCore của người dùng ───────────────

export function useLienKet(bat: boolean) {
  return useQuery<LienKetOpenCore, ApiError>({
    queryKey: KHOA_RPA.lienKet,
    queryFn: () => repo.lienKet(),
    enabled: bat,
    staleTime: 15_000,
    refetchInterval: CHU_KY_LIEN_KET_MS,
    retry: thuLai,
  });
}

/** Đổi tài khoản là đổi Project được phép chạy: bỏ mọi tuỳ chọn chạy đã đệm. */
function useSauLienKet() {
  const qc = useQueryClient();
  return (moi: LienKetOpenCore) => {
    qc.setQueryData(KHOA_RPA.lienKet, moi);
    void qc.invalidateQueries({ queryKey: ['rpa', 'chay'] });
  };
}

export function useMoLienKet() {
  const sau = useSauLienKet();
  return useMutation<LienKetOpenCore, ApiError, { username: string; password: string }>({
    mutationFn: (than) => repo.moLienKet(than),
    onSuccess: sau,
  });
}

export function useNgatLienKet() {
  const sau = useSauLienKet();
  return useMutation<LienKetOpenCore, ApiError, void>({ mutationFn: () => repo.ngatLienKet(), onSuccess: sau });
}

// ─────────────── Chạy Workflow ───────────────

/** Hỏi Project được phép chạy ngay lúc bấm nút — không đệm, License hay kết nối có thể vừa đổi. */
export function useHoiTuyChonChay(): (id: string) => Promise<TuyChonChay> {
  const qc = useQueryClient();
  return useCallback(
    (id: string) => qc.fetchQuery({ queryKey: KHOA_RPA.tuyChonChay(id), queryFn: () => repo.tuyChonChay(id), staleTime: 0, retry: false }),
    [qc],
  );
}

export function useBatDauChay(id: string) {
  const qc = useQueryClient();
  return useMutation<LuotChay, ApiError, { projectId: string }>({
    mutationFn: ({ projectId }) => repo.batDauChay(id, { projectId, inputData: {} }),
    onSettled: () => void qc.invalidateQueries({ queryKey: KHOA_RPA.luotGanNhat(id) }),
  });
}

/** Lượt gần nhất của Workflow; hỏi lại mỗi 3 giây khi còn đang chạy, thôi khi xong. */
export function useLuotGanNhat(id: string, bat: boolean) {
  return useQuery<LuotGanNhat, ApiError>({
    queryKey: KHOA_RPA.luotGanNhat(id),
    queryFn: () => repo.luotGanNhat(id),
    enabled: bat && id !== '',
    staleTime: 0,
    refetchInterval: (q) => (q.state.data?.active ? CHU_KY_LUOT_MS : false),
    retry: thuLai,
  });
}
